/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * GlossaryView — the canonical "what does everything mean" reference page.
 *
 * Shows every glossary term grouped by category. Listens for the
 * 'infotip:navigate' CustomEvent so any tooltip's "Learn more →" link can
 * scroll the matching entry into view and briefly highlight it.
 *
 * No backend dependency — all content is in src/lib/glossary.ts.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { listTermsByCategory, GLOSSARY } from '@/lib/glossary';
import { ViewHeader } from '@/components/ViewHeader';

type Props = {
  onBack: () => void;
  /**
   * If set, the view will scroll-to + highlight this term on mount and on
   * any prop change. Used by App.tsx to forward the term id from a tooltip
   * "Learn more →" click that originated outside the glossary view.
   */
  initialTermId?: string | null;
};

export const GlossaryView: React.FC<Props> = ({ onBack, initialTermId }) => {
  const groups = useMemo(() => listTermsByCategory(), []);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  // Forward-prop scroll: when App.tsx hands us a term id (because the user
  // clicked "Learn more →" from a tooltip while on a different view), we
  // need to wait one frame for the DOM to settle, then scroll-to + highlight.
  useEffect(() => {
    if (!initialTermId || !GLOSSARY[initialTermId]) return;
    const id = initialTermId;
    const t = window.setTimeout(() => {
      const el = document.getElementById(`glossary-term-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightId(id);
        window.setTimeout(() => setHighlightId(null), 2500);
      }
    }, 50);
    return () => window.clearTimeout(t);
  }, [initialTermId]);

  // In-page listener too — handles "Learn more" clicks from a tooltip that
  // happens to be open while the user is already on the glossary view.
  useEffect(() => {
    function handler(e: any) {
      const id = e?.detail?.termId;
      if (!id) return;
      const el = document.getElementById(`glossary-term-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightId(id);
        window.setTimeout(() => setHighlightId(null), 2000);
      }
    }
    window.addEventListener('infotip:navigate', handler);
    return () => window.removeEventListener('infotip:navigate', handler);
  }, []);

  // Apply scroll highlight even on first mount if URL hash present.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash?.replace('#', '');
    if (hash && GLOSSARY[hash]) {
      const el = document.getElementById(`glossary-term-${hash}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightId(hash);
        window.setTimeout(() => setHighlightId(null), 2500);
      }
    }
  }, []);

  const q = query.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        terms: g.terms.filter(
          (t) =>
            t.title.toLowerCase().includes(q) ||
            t.short.toLowerCase().includes(q) ||
            t.long.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.terms.length > 0);
  }, [groups, q]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <ViewHeader
        title="Glossary"
        tagline="Every metric, tier, and term in the dashboard, defined."
      />

      {/* Search */}
      <div className="bg-bg-raised rounded-xl border border-border-base p-4">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search terms…"
          className="w-full bg-bg-base border border-border-base rounded-lg px-3 py-2 text-fg-primary placeholder-fg-muted"
        />
      </div>

      {/* Category jump nav */}
      <div className="flex flex-wrap gap-2">
        {filteredGroups.map((g) => (
          <a
            key={g.category}
            href={`#category-${g.category}`}
            className="text-xs font-semibold uppercase tracking-wide bg-bg-raised hover:bg-bg-elevated border border-border-base hover:border-border-subtle text-fg-secondary px-3 py-1.5 rounded-full inline-flex items-center justify-center min-h-11 sm:min-h-0"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById(`category-${g.category}`);
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            {g.label}
          </a>
        ))}
      </div>

      {/* Categories */}
      {filteredGroups.length === 0 && (
        <div className="rounded-xl border border-border-base bg-bg-raised/60 p-8 text-center text-fg-muted">
          No terms match "{query}".
        </div>
      )}

      {filteredGroups.map((group) => (
        <section
          key={group.category}
          id={`category-${group.category}`}
          className="bg-bg-raised rounded-xl border border-border-base p-6"
        >
          <h2 className="text-lg font-bold text-fg-primary mb-4 flex items-center gap-2">
            <span
              className="inline-block w-1 h-5 bg-accent rounded"
              aria-hidden
            />
            {group.label}
          </h2>
          <div className="space-y-4">
            {group.terms.map((t) => {
              const isHL = highlightId === t.id;
              return (
                <div
                  key={t.id}
                  id={`glossary-term-${t.id}`}
                  className={`rounded-lg border p-4 transition ${
                    isHL
                      ? 'border-accent bg-status-danger-bg/30 shadow-lg shadow-status-danger-bg/40'
                      : 'border-border-base bg-bg-base/60'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-base font-bold text-fg-primary">{t.title}</h3>
                    <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold flex-shrink-0">
                      {t.category}
                    </span>
                  </div>
                  <p className="text-sm text-fg-secondary leading-relaxed">{t.short}</p>

                  {t.long && (
                    <div className="mt-3 text-sm text-fg-muted leading-relaxed whitespace-pre-line">
                      {t.long}
                    </div>
                  )}

                  {t.formula && (
                    <div className="mt-3 rounded bg-bg-base border border-border-base px-3 py-2 font-mono text-xs text-status-success overflow-x-auto">
                      {t.formula}
                    </div>
                  )}

                  {t.swatches && t.swatches.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {t.swatches.map((s, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-2 rounded bg-bg-raised border border-border-base px-2 py-1 text-xs text-fg-secondary"
                        >
                          <span
                            className="inline-block w-3 h-3 rounded-sm"
                            style={{ backgroundColor: s.color }}
                          />
                          {s.label}
                        </span>
                      ))}
                    </div>
                  )}

                  {t.examples && t.examples.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mb-1">
                        Examples
                      </div>
                      <ul className="space-y-1 text-sm text-fg-secondary">
                        {t.examples.map((ex, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-fg-muted">•</span>
                            <span>{ex}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {t.seeAlso && t.seeAlso.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-border-base/60">
                      <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold mr-2">
                        See also:
                      </span>
                      {t.seeAlso.map((id) => {
                        const ref = GLOSSARY[id];
                        if (!ref) return null;
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => {
                              const el = document.getElementById(`glossary-term-${id}`);
                              el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                              setHighlightId(id);
                              window.setTimeout(() => setHighlightId(null), 2000);
                            }}
                            className="text-xs text-accent-fg hover:text-status-danger mr-3 inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
                          >
                            {ref.title}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default GlossaryView;
